import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CountUp from 'react-countup';
import {
    FaBriefcase,
    FaPiggyBank,
    FaHandHoldingUsd,
    FaChartLine,
    FaWallet,
    FaPercentage,
    FaCoins,
    FaChartBar,
    FaUsers,
    FaCalendarAlt,
    FaBuilding
} from 'react-icons/fa';

const Stats = ({ showAll = false }) => {
    const { t } = useTranslation();
    const [started, setStarted] = useState(false);
    const sectionRef = useRef(null);

    const allStats = [
        {
            id: 1,
            end: 43,
            prefix: '₹',
            suffix: t('stats.cr'),
            labelKey: 'stats.totalBusiness',
            duration: 2.5,
            decimals: 0,
            icon: <FaBriefcase />,
            category: 'business'
        },
        {
            id: 2,
            end: 25.13,
            prefix: '₹',
            suffix: t('stats.cr'),
            labelKey: 'stats.totalDeposits',
            duration: 2.5,
            decimals: 2,
            icon: <FaPiggyBank />,
            category: 'business'
        },
        {
            id: 3,
            end: 16.49,
            prefix: '₹',
            suffix: t('stats.cr'),
            labelKey: 'stats.loanDisbursed',
            duration: 2.5,
            decimals: 2,
            icon: <FaHandHoldingUsd />,
            category: 'business'
        },
        {
            id: 4,
            end: 9.40,
            prefix: '₹',
            suffix: t('stats.cr'),
            labelKey: 'stats.investment',
            duration: 2.5,
            decimals: 2,
            icon: <FaChartLine />,
            category: 'business'
        },
        {
            id: 5,
            end: 1.13,
            prefix: '₹',
            suffix: t('stats.cr'),
            labelKey: 'stats.ownFund',
            duration: 2.5,
            decimals: 2,
            icon: <FaWallet />,
            category: 'reserves'
        },
        {
            id: 6,
            end: 59.41,
            prefix: '₹',
            suffix: t('stats.lakh'),
            labelKey: 'stats.shareCapital',
            duration: 2.5,
            decimals: 2,
            icon: <FaCoins />,
            category: 'reserves'
        },
        {
            id: 7,
            end: 24.13,
            prefix: '₹',
            suffix: t('stats.lakh'),
            labelKey: 'stats.profit',
            duration: 2.5,
            decimals: 2,
            icon: <FaChartBar />,
            category: 'reserves'
        },
        {
            id: 8,
            end: 11,
            suffix: t('stats.percent'),
            labelKey: 'stats.dividend',
            duration: 2,
            decimals: 0,
            icon: <FaPercentage />,
            category: 'reserves'
        },
        {
            id: 9,
            end: 2500,
            suffix: '+',
            labelKey: 'stats.members',
            duration: 3,
            separator: ',',
            icon: <FaUsers />,
            category: 'member'
        },
        {
            id: 10,
            end: 33,
            suffix: '+',
            labelKey: 'stats.years',
            duration: 2.5,
            icon: <FaCalendarAlt />,
            category: 'member'
        },
        {
            id: 11,
            end: 3,
            suffix: '+',
            labelKey: 'stats.branches',
            duration: 2,
            icon: <FaBuilding />,
            category: 'member'
        }
    ];

    const displayStats = showAll
        ? allStats
        : [
            allStats.find((s) => s.id === 10), // Years of Service (33+)
            allStats.find((s) => s.id === 9),  // Happy Members (2,500+)
            allStats.find((s) => s.id === 1),  // Total Business (₹43 Cr)
            allStats.find((s) => s.id === 2),  // Total Deposits (₹25.13 Cr)
            allStats.find((s) => s.id === 3)   // Loan Disbursed (₹16.49 Cr)
        ];

    const handleRef = (node) => {
        if (!node) return;
        sectionRef.current = node;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started) {
                    setStarted(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 }
        );
        observer.observe(node);
    };

    if (showAll) {
        return (
            <section className="py-20 bg-gray-50" ref={handleRef}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {allStats.map((stat) => (
                            <div
                                key={stat.id}
                                className="bg-white rounded-xl shadow-md border border-gray-100 p-6 flex items-center gap-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                            >
                                <div className="w-14 h-14 rounded-xl bg-blue-50 text-primary flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                    {stat.icon}
                                </div>
                                <div>
                                    <div className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">
                                        {started ? (
                                            <CountUp
                                                start={0}
                                                end={stat.end}
                                                duration={stat.duration}
                                                separator={stat.separator || ''}
                                                prefix={stat.prefix || ''}
                                                suffix={stat.suffix || ''}
                                                decimals={stat.decimals || 0}
                                            />
                                        ) : (
                                            <span>{stat.prefix || ''}0{stat.suffix || ''}</span>
                                        )}
                                    </div>
                                    <div className="text-xs md:text-sm text-gray-500 font-bold uppercase tracking-wider mt-1 group-hover:text-secondary transition-colors">
                                        {t(stat.labelKey)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="relative mt-12 md:mt-16 z-20 pb-12 md:pb-16" ref={handleRef}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-2xl p-6 md:p-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 divide-x divide-gray-100 border-t-4 border-secondary">
                    {displayStats.map((stat) => (
                        <div
                            key={stat.id}
                            className="text-center group px-4"
                        >
                            <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-2 group-hover:-translate-y-1 transition-transform duration-300 whitespace-nowrap">
                                {started ? (
                                    <CountUp
                                        start={0}
                                        end={stat.end}
                                        duration={stat.duration}
                                        separator={stat.separator || ''}
                                        prefix={stat.prefix || ''}
                                        suffix={stat.suffix || ''}
                                        decimals={stat.decimals || 0}
                                    />
                                ) : (
                                    <span>{stat.prefix || ''}0{stat.suffix || ''}</span>
                                )}
                            </div>
                            <div className="text-xs md:text-sm text-gray-500 font-bold uppercase tracking-widest mt-2 group-hover:text-secondary transition-colors">
                                {t(stat.labelKey)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Stats;
